import React, { useEffect, useState, useRef, useId } from 'react';
import { Dialog } from '@headlessui/react';
import { useClub } from '../context/ClubContext';
import { createYocoCheckout, createIkhokhaCheckout, createZapperHostedCheckout, startPendingPayment, getClubDetails } from '../api';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  totalPaymentAmount = 0, 
  reference, 
  onPaymentComplete,
  customText = null,
  showTimer = true,
  timerDuration = 20,
  paymentDetails = null
}) => {
  const { clubDetails, setClubDetails } = useClub();
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [isLoadingClubDetails, setIsLoadingClubDetails] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(`${timerDuration}:00`);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [isCreatingCheckoutSession, setIsCreatingCheckoutSession] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [zapperAltPaymentActive, setZapperAltPaymentActive] = useState(false);
  const widgetCreatedRef = useRef(false);
  const pendingPaymentStartedRef = useRef(false);
  const zapperWidgetRef = useRef(null);
  const paymentWindowRef = useRef(null);
  const paymentCheckIntervalRef = useRef(null);
  const onPaymentCompleteRef = useRef(onPaymentComplete);
  const onCloseRef = useRef(onClose);
  const paymentContainerRef = useRef(null);
  const paymentContainerId = `payment-container-${useId().replace(/:/g, '')}`;
  const paymentProvider = clubDetails.paymentProvider;
  const isPaymentProviderConfigured = ['zapper', 'yoco', 'ikhokha', 'zapper_hosted'].includes(paymentProvider);
  const isZapper = paymentProvider === 'zapper';
  const isYoco = paymentProvider === 'yoco';
  const isIkhokha = paymentProvider === 'ikhokha';
  const isZapperHosted = paymentProvider === 'zapper_hosted';
  const isHostedCheckoutFlow = isYoco || isIkhokha || isZapperHosted || (isZapper && zapperAltPaymentActive);
  const isJoinPayment = paymentDetails?.type === 'public_join';
  const isBookingPayment = paymentDetails?.response?.successfulBookings?.length > 0;

  const buildPendingPaymentPayload = () => {
    if (!paymentDetails || totalPaymentAmount <= 0 || !reference) return null;

    const paymentType = paymentDetails.type
      || (paymentDetails.userDisplayName && !paymentDetails.type ? 'membership_renewal' : null);

    if (!['membership_renewal', 'membership_signup', 'credit_purchase'].includes(paymentType)) {
      return null;
    }

    return {
      type: paymentType,
      reference,
      amount: Number(totalPaymentAmount),
      targetUserTypeId: paymentDetails.targetUserTypeId || null,
      creditAmount: paymentDetails.creditAmount || null
    };
  };
  const totalFee = (() => {
    if (isJoinPayment) {
      return Number(paymentDetails?.fullFee ?? 0) || 0;
    }
    if (isBookingPayment) {
      return paymentDetails.response.successfulBookings.reduce((sum, booking) => {
        const feeValue = parseFloat(booking?.bookingFee ?? 0);
        return sum + (Number.isNaN(feeValue) ? 0 : feeValue);
      }, 0);
    }
    return null;
  })();
  const creditsApplied = totalFee !== null
    ? Math.max(0, parseFloat((totalFee - totalPaymentAmount).toFixed(2)))
    : 0;
  const showCreditBreakdown = totalFee !== null && creditsApplied > 0;

  const destroyPaymentWidget = () => {
    try {
      if (zapperWidgetRef.current && typeof zapperWidgetRef.current.destroy === 'function') {
        zapperWidgetRef.current.destroy();
      }
    } catch (error) {
      console.error('Error destroying payment widget:', error);
    } finally {
      zapperWidgetRef.current = null;
      widgetCreatedRef.current = false;
      if (paymentContainerRef.current) {
        paymentContainerRef.current.innerHTML = '';
      }
    }
  };

  const getPaymentWindowFeatures = (providerForWindow = paymentProvider) => {
    const isZapperHostedWindow = providerForWindow === 'zapper_hosted';
    const defaultWidth = isZapperHostedWindow ? 1200 : 600;
    const defaultHeight = isZapperHostedWindow ? 900 : 800;
    const maxWidth = Math.max(480, window.screen.availWidth - 24);
    const maxHeight = Math.max(640, window.screen.availHeight - 24);
    const width = Math.min(defaultWidth, maxWidth);
    const height = Math.min(defaultHeight, maxHeight);
    const left = Math.max(0, Math.floor((window.screen.availWidth - width) / 2));
    const top = Math.max(0, Math.floor((window.screen.availHeight - height) / 2));
    return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  };

  useEffect(() => {
    if (!isOpen) {
      setIsLoadingClubDetails(false);
      return undefined;
    }

    if (isPaymentProviderConfigured) {
      return undefined;
    }

    let cancelled = false;
    setIsLoadingClubDetails(true);

    getClubDetails()
      .then((details) => {
        if (!cancelled && details) {
          setClubDetails(details);
        }
      })
      .catch((error) => {
        console.error('[PaymentModal] Failed to load club details:', error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingClubDetails(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isPaymentProviderConfigured, setClubDetails]);

  useEffect(() => {
    onPaymentCompleteRef.current = onPaymentComplete;
  }, [onPaymentComplete]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      // Reset transient payment state when modal closes.
      destroyPaymentWidget();
      setPaymentError(null);
      setPaymentSuccessful(false);
      setCheckoutUrl(null);
      setZapperAltPaymentActive(false);

      if (paymentCheckIntervalRef.current) {
        clearInterval(paymentCheckIntervalRef.current);
        paymentCheckIntervalRef.current = null;
      }

      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
      paymentWindowRef.current = null;
      return;
    }

    if (!showTimer) return;

    // Reset timer when modal opens
    setTimeRemaining(`${timerDuration}:00`);
    const startTime = Date.now();
    const endTime = startTime + (timerDuration * 60 * 1000); // timerDuration minutes in milliseconds

    const timer = setInterval(() => {
      const now = Date.now();
      const diff = endTime - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        clearInterval(timer);
        onCloseRef.current?.();
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, showTimer, timerDuration]);

  // Create checkout session (Yoco or iKhokha)
  const createCheckoutSession = async (providerOverride = null) => {
    setIsCreatingCheckoutSession(true);
    setPaymentError(null); // Clear any previous errors
    const checkoutProvider = providerOverride || (isHostedCheckoutFlow ? (isZapper ? 'zapper_hosted' : paymentProvider) : paymentProvider);
    
    // Open a blank window immediately while user gesture is still valid
    // This prevents popup blockers from blocking the window
    const popupFeatures = getPaymentWindowFeatures(checkoutProvider);
    const paymentWindow = window.open('', '_blank', popupFeatures);
    
    if (!paymentWindow) {
      // Popup was blocked - show error to user
      setIsCreatingCheckoutSession(false);
      setPaymentError('Popup was blocked. Please allow popups for this site and try again.');
      return;
    }
    
    // Store reference to the window
    paymentWindowRef.current = paymentWindow;
    
    // Show loading message in the popup
    paymentWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Loading Payment...</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              background: #f5f5f5;
              min-height: 100vh;
              overflow: auto;
            }
            .loader {
              position: fixed;
              inset: 0;
              display: grid;
              place-items: center;
              text-align: center;
              padding: 16px;
            }
            .spinner {
              border: 4px solid #f3f3f3;
              border-top: 4px solid #3498db;
              border-radius: 50%;
              width: clamp(40px, 10vw, 56px);
              height: clamp(40px, 10vw, 56px);
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            .loader p {
              margin: 0;
              font-size: clamp(14px, 3.8vw, 16px);
              color: #374151;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="loader">
            <div class="spinner"></div>
            <p>Loading payment page...</p>
          </div>
        </body>
      </html>
    `);
    
    try {
      // Determine metadata based on payment type
      let metadata = {};
      
      // Check if this is a credit purchase, membership renewal (from Sidebar), booking (from Dashboard), or join payment
      if (paymentDetails?.type === 'credit_purchase') {
        // This is a credit purchase from Sidebar
        metadata = {
          type: 'credit_purchase',
          userUuid: paymentDetails.userUuid,
          creditAmount: paymentDetails.creditAmount
        };
      } else if (paymentDetails?.type === 'membership_signup') {
        metadata = {
          type: 'membership_renewal',
          userUuid: paymentDetails.userUuid,
          targetUserTypeId: paymentDetails.targetUserTypeId
        };
      } else if (
        paymentDetails?.type === 'membership_renewal'
        || (paymentDetails?.userDisplayName && !paymentDetails?.type)
      ) {
        // Membership renewal — Sidebar Renew Now or Memberships page expired renewal
        metadata = {
          type: 'membership_renewal',
          userUuid: paymentDetails.userUuid
        };
      } else if (paymentDetails?.type === 'public_join') {
      // This is a join payment for a public booking
      metadata = {
        type: 'public_join',
        bookingId: paymentDetails.bookingId,
        userUuid: paymentDetails.userUuid,
        bookingDate: paymentDetails.bookingDate,
        courtStartTime: paymentDetails.courtStartTime
      };
      } else if (paymentDetails?.response?.successfulBookings?.length > 0) {
        // This is a booking from Dashboard
        const firstBooking = paymentDetails.response.successfulBookings[0];
        metadata = {
          type: 'booking',
          bookingDate: firstBooking.date
        };
      }
      
      const provider = checkoutProvider;
      const paymentType = metadata.type || 'booking';
      console.log('Payment reference for checkout:', reference, `(${reference?.length ?? 0} chars)`, { provider: checkoutProvider });

      const checkoutData = {
        amount: Math.round(totalPaymentAmount * 100), // Convert to cents
        currency: 'ZAR',
        reference: reference,
        successUrl: `${window.location.origin}/payment-success.html?reference=${reference}&provider=${provider}&type=${paymentType}`,
        cancelUrl: `${window.location.origin}/payment-cancelled.html?reference=${reference}&provider=${provider}&type=${paymentType}`,
        failureUrl: `${window.location.origin}/payment-failed.html?reference=${reference}&provider=${provider}&type=${paymentType}`,
        metadata: metadata
      };

      let data;
      if (provider === 'yoco') {
        data = await createYocoCheckout(checkoutData);
      } else if (provider === 'ikhokha') {
        data = await createIkhokhaCheckout(checkoutData);
      } else if (provider === 'zapper_hosted') {
        data = await createZapperHostedCheckout(checkoutData);
      } else {
        throw new Error(`Unsupported checkout provider: ${provider}`);
      }

      if (data.redirectUrl) {
        setCheckoutUrl(data.redirectUrl);
        // Navigate the already-opened window to the payment URL
        paymentWindow.location.href = data.redirectUrl;

        if (paymentCheckIntervalRef.current) {
          clearInterval(paymentCheckIntervalRef.current);
          paymentCheckIntervalRef.current = null;
        }
        
        // Start polling for payment completion as backup
        const interval = setInterval(async () => {
          try {
            // Check if payment window is closed
            if (paymentWindow && paymentWindow.closed) {
              clearInterval(interval);
              paymentCheckIntervalRef.current = null;
              
              // Check if payment was successful by calling the backend
              // This is a fallback in case postMessage doesn't work
              console.log('Payment window closed, checking payment status...');
              
              // For now, let's just show a message to the user
              // In a real implementation, you could call an API to check payment status
              console.log('Payment window closed - user may have completed payment');
              
              // You could add a manual refresh button or automatic status check here
              // For now, we'll rely on the postMessage mechanism
            }
          } catch (error) {
            console.error('Error checking payment status:', error);
          }
        }, 2000); // Check every 2 seconds
        
        paymentCheckIntervalRef.current = interval;
      } else {
        // Close the window if we failed to get a redirect URL
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        paymentWindowRef.current = null;
        console.error('Failed to create checkout session:', data);
        setPaymentError('Failed to create payment session. Please try again or contact the club for assistance.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      // Close the window if there was an error
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
      paymentWindowRef.current = null;
      
      // Set user-friendly error message based on the error
      let errorMessage = 'Payment system is currently unavailable. Please try again or contact the club for assistance.';
      
      if (error.message) {
        if (error.message.includes('400')) {
          errorMessage = 'Payment configuration error. Please contact the club to set up payment processing.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Payment authentication failed. Please contact the club for assistance.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Payment server error. Please try again in a few minutes.';
        }
      }
      
      setPaymentError(errorMessage);
    } finally {
      setIsCreatingCheckoutSession(false);
    }
  };

  const handleZapperAlternativePayment = async () => {
    destroyPaymentWidget();
    setZapperAltPaymentActive(true);
    await createCheckoutSession('zapper_hosted');
  };

  useEffect(() => {
    if (isOpen && totalPaymentAmount > 0 && reference && isZapper && !zapperAltPaymentActive) {
      const pendingPayload = buildPendingPaymentPayload();
      if (pendingPayload && !pendingPaymentStartedRef.current) {
        pendingPaymentStartedRef.current = true;
        startPendingPayment(pendingPayload).catch((error) => {
          console.error('[PaymentModal] Failed to store pending payment:', error);
        });
      }
    }

    if (!isOpen) {
      pendingPaymentStartedRef.current = false;
    }
  }, [isOpen, totalPaymentAmount, reference, isZapper, zapperAltPaymentActive, paymentDetails]);

  useEffect(() => {
    if (isOpen && totalPaymentAmount > 0 && reference && !widgetCreatedRef.current) {
      if (isZapper && !zapperAltPaymentActive) {
        try {
          const now = new Date().toLocaleTimeString();
          console.log(`[${now}] ************ GENERATING ZAPPER PAYMENT WIDGET!!!!!!!!!!`);

          if (paymentContainerRef.current) {
            paymentContainerRef.current.innerHTML = '';
          }

          const paymentWidget = new window.zapper.payments.PaymentWidget(`#${paymentContainerId}`, {
            merchantId: clubDetails.zapperMerchantId,
            siteId: clubDetails.zapperSiteId,
            amount: Number(totalPaymentAmount),
            reference
          });
          zapperWidgetRef.current = paymentWidget;

          paymentWidget.on('payment', ({ status, paidAmount, zapperId }) => {
            const now = new Date().toLocaleTimeString();
            console.log(`[${now}]  ***** Payment received with status ${status} of ${paidAmount} and zapperId ${zapperId} FOR reference ${reference}!`);

            // If payment is successful (status = 2), notify parent component
            if (status === 2) {
              setPaymentSuccessful(true);
              onPaymentCompleteRef.current?.();
            }
          });
          
          widgetCreatedRef.current = true;
          
          // Cleanup function to destroy the widget when component unmounts or dependencies change
          return () => {
            destroyPaymentWidget();
          };
        } catch (error) {
          console.error('Error initializing payment widget:', error);
        }
      } else if (isHostedCheckoutFlow) {
        // For hosted providers, set up message listener for payment completion
        const expectedProvider = isZapper && zapperAltPaymentActive ? 'zapper_hosted' : paymentProvider;
        const handleMessage = (event) => {
          console.log('Received message in PaymentModal:', event.data, 'Origin:', event.origin);
          const data = event.data || {};
          const isMatch =
            data.reference === reference &&
            (
              data.type === 'YOCO_PAYMENT_SUCCESS' ||
              data.type === 'IKHOKHA_PAYMENT_SUCCESS' ||
              (data.type === 'PAYMENT_SUCCESS' && (!data.provider || data.provider === expectedProvider))
            );

          if (isMatch) {
            console.log('Payment successful via message:', data);
            setPaymentSuccessful(true);
            onPaymentCompleteRef.current?.();
          }
        };

        window.addEventListener('message', handleMessage);
        console.log(`${expectedProvider} payment provider selected, message listener added for reference:`, reference);

        return () => {
          window.removeEventListener('message', handleMessage);
        };
      }
    }

    return undefined;
  }, [
    isOpen,
    totalPaymentAmount,
    reference,
    paymentProvider,
    isHostedCheckoutFlow,
    isZapper,
    zapperAltPaymentActive,
    clubDetails.zapperMerchantId,
    clubDetails.zapperSiteId,
    paymentContainerId
  ]);

  useEffect(() => {
    return () => {
      if (paymentCheckIntervalRef.current) {
        clearInterval(paymentCheckIntervalRef.current);
        paymentCheckIntervalRef.current = null;
      }
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
      paymentWindowRef.current = null;
    };
  }, []);

  if (!totalPaymentAmount || !reference) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed z-[1000] inset-0 overflow-y-auto pointer-events-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <Dialog.Panel className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-4">
          <span className="flex items-center">
            <span className="text-red-600 mr-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
                <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                <circle cx="12" cy="17" r="1.2" fill="white" />
              </svg>
            </span>
            <span>Payment Required</span>
          </span>
          </Dialog.Title>

          <div className="mb-4 text-slate-600">
            {customText && !isBookingPayment && (
              <div
                className="text-xs leading-snug sm:text-sm sm:leading-normal [&_ul]:space-y-1.5 sm:[&_ul]:space-y-2"
                dangerouslySetInnerHTML={{ __html: customText }}
              />
            )}
            {!paymentSuccessful && showTimer && (
              <p>
                Booking will be cancelled if not paid in{' '}
                <span className="text-red-600 font-bold">{timeRemaining}</span>
              </p>
            )}
          </div>

          <div className="relative mb-4">
            {showCreditBreakdown && (
              <div className="mb-3 text-sm text-slate-600 space-y-1">
                <div className="flex items-center justify-between">
                  <span>Total fee</span>
                  <span className="font-medium">R{totalFee.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-blue-700">
                  <span>Credits applied</span>
                  <span className="font-medium">-R{creditsApplied.toFixed(2)}</span>
                </div>
              </div>
            )}
            {showCreditBreakdown && <div className="border-t border-slate-200 mb-3" />}
            {isPaymentProviderConfigured && isZapper && !zapperAltPaymentActive ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
                <div
                  ref={paymentContainerRef}
                  id={paymentContainerId}
                  className="flex justify-center items-center min-h-[200px] w-full"
                />
                {!paymentSuccessful && (
                  <>
                    <p className="text-xs text-slate-600 text-center">
                      Don't have Zapper?
                    </p>
                    <button
                      onClick={handleZapperAlternativePayment}
                      disabled={isCreatingCheckoutSession}
                      className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreatingCheckoutSession ? 'Creating Payment Session...' : 'Other Payment Methods'}
                    </button>
                  </>
                )}
              </div>
            ) : isPaymentProviderConfigured && isHostedCheckoutFlow ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
                <div className="text-center">
                  <p className="text-lg font-medium text-slate-600 mb-2">
                    Payment due: R{totalPaymentAmount.toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-600 mb-4">
                    Reference: {reference}
                  </p>
                </div>
                
                {!paymentSuccessful ? (
                  <>
                    <button
                      onClick={() => createCheckoutSession(isZapper ? 'zapper_hosted' : paymentProvider)}
                      disabled={isCreatingCheckoutSession}
                      className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreatingCheckoutSession ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating Payment Session...
                        </span>
                      ) : (
                        paymentProvider === 'yoco'
                          ? 'Pay with Yoco'
                          : paymentProvider === 'ikhokha'
                            ? 'Pay with iKhokha'
                            : 'Other Payment Methods' // Zapper Hosted
                      )}
                    </button>
                    {paymentError && (
                      <div className="text-center space-y-2">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center justify-center space-x-2 text-red-600 mb-2">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="font-medium">Payment Error</span>
                          </div>
                          <p className="text-sm text-red-700">{paymentError}</p>
                          <button
                            onClick={() => setPaymentError(null)}
                            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {checkoutUrl && (
                      <div className="text-center space-y-2">
                        <div className="flex items-center justify-center space-x-2 text-blue-600">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm font-medium">Payment window opened</span>
                        </div>
                        <p className="text-xs text-slate-600">
                          Complete your payment in the new window. This window will close automatically when payment is complete.
                        </p>
                        <button
                          onClick={() => {
                            const reopenProvider = isZapper ? 'zapper_hosted' : paymentProvider;
                            const reopenFeaturesWithProvider = getPaymentWindowFeatures(reopenProvider);
                            window.open(checkoutUrl, '_blank', reopenFeaturesWithProvider);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Reopen payment window
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-lg font-medium">Payment Successful!</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {(() => {
                        const type = paymentDetails?.type || 'booking';
                        if (type === 'membership_renewal') return 'Your membership has been renewed.';
                        if (type === 'membership_signup') return 'Your membership signup has been completed.';
                        if (type === 'credit_purchase') return 'Your credits have been updated.';
                        if (type === 'public_join') return 'Your spot has been confirmed.';
                        return 'Your booking has been confirmed.';
                      })()}
                    </p>
                  </div>
                )}
              </div>
            ) : isLoadingClubDetails ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <p className="text-slate-600">Loading payment options...</p>
              </div>
            ) : (
              <div className="flex justify-center items-center min-h-[200px]">
                <p className="text-slate-600">Payment provider not configured</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
      
    </Dialog>
  );
};

export default PaymentModal;
